import type { Client, CombinedError } from "urql";
import type { WorkspaceSidebarData } from "./workspace-sidebar-data-model";
import {
  WorkspaceSidebarCoreDataQuery,
  WorkspaceSidebarDashboardsDataQuery,
  WorkspaceSidebarDataQuery,
  WorkspaceSidebarProjectsDataQuery,
} from "./workspace-sidebar-data-query";

/**
 * network errorを含まず、全GraphQL errorがOnboarding session root fieldだけを指す場合にtrue。
 * path不明、複数domain、network errorはfalseとし、core fallbackの誤適用を防ぐ。
 */
export function isOnboardingOnlyError(error: CombinedError): boolean {
  return (
    !error.networkError &&
    error.graphQLErrors.length > 0 &&
    error.graphQLErrors.every((graphQLError) => {
      const rootField = graphQLError.path?.[0];
      return (
        typeof rootField === "string" &&
        rootField.includes("OnboardingSessions")
      );
    })
  );
}

/**
 * network errorを含まず、全GraphQL errorがDashboard root fieldだけを指す場合にtrue。
 * pathが欠ける、複数domainへまたがる、またはnetwork errorを含む場合はfalseとして、
 * 不完全なfallback dataを成功snapshotとして扱わない。
 */
export function isDashboardOnlyError(error: CombinedError): boolean {
  return (
    !error.networkError &&
    error.graphQLErrors.length > 0 &&
    error.graphQLErrors.every(
      (graphQLError) =>
        graphQLError.path?.[0] === "node" &&
        graphQLError.path?.[1] === "dashboards",
    )
  );
}

/**
 * network errorを含まず、全GraphQL errorがOrganization配下のProject domainだけを
 * 指す場合にtrue。Dashboard field、path不明、複数domain、network errorはfalseとし、
 * Dashboard fallbackをProject-only障害以外へ適用しない。
 */
export function isProjectOnlyError(error: CombinedError): boolean {
  return (
    !error.networkError &&
    error.graphQLErrors.length > 0 &&
    error.graphQLErrors.every((graphQLError) => {
      const path = graphQLError.path;
      return (
        path?.[0] === "node" &&
        path[1] !== "dashboards" &&
        path[1] !== "dashboardOrder"
      );
    })
  );
}

function dashboardSnapshot(data: WorkspaceSidebarData | null) {
  const node = data?.node;
  if (node?.__typename !== "Organization") {
    return { dashboardOrder: null, dashboards: [] };
  }
  return {
    dashboardOrder: node.dashboardOrder,
    dashboards: node.dashboards,
  };
}

function projectSnapshot(data: WorkspaceSidebarData | null) {
  const node = data?.node;
  if (node?.__typename !== "Organization") {
    return {
      enabledFeatures: [],
      projectOrderIW: null,
      projectOrderSA: null,
      projectOrderUA: null,
      projectOrderAA: null,
      projects: [],
    };
  }
  // gql.tadaはfragment fieldを型上maskするが、response object自体には値がある。
  // nodeをそのまま保持してfragment参照とProject fieldをまとめて引き継ぐ。
  return node;
}

/**
 * 統合queryを通常経路として実行し、単一domainのresolver障害時だけ補助queryへ退避する。
 * Dashboard障害ではProject、Project障害ではDashboard、Onboarding障害ではcore全体を
 * 回収し、直前snapshotと合成したpartial dataを元のerrorと共に返す。回収不能、複数domain、
 * network errorでは安全なdataを作らず、呼び出し側が直前snapshotを維持できる結果を返す。
 */
export async function fetchWorkspaceSidebarData(
  client: Client,
  workspaceId: string,
  getLatestData: () => WorkspaceSidebarData | null,
) {
  const result = await client
    .query(
      WorkspaceSidebarDataQuery,
      { organizationId: workspaceId },
      { requestPolicy: "network-only" },
    )
    .toPromise();

  if (result.error && isDashboardOnlyError(result.error)) {
    // dashboardsはschema上non-nullのため、resolver障害がOrganization node全体へ
    // 伝播する。障害時だけProject fragmentを別queryで回収し、refreshなら直前の
    // Dashboard snapshotを維持することで、無関係なProject navigationを消さない。
    const projectsResult = await client
      .query(
        WorkspaceSidebarProjectsDataQuery,
        { organizationId: workspaceId },
        { requestPolicy: "network-only" },
      )
      .toPromise();
    if (projectsResult.data?.node && !projectsResult.error) {
      const latestData = getLatestData();
      return {
        data: {
          ...(result.data ?? latestData),
          node: {
            ...projectsResult.data.node,
            ...dashboardSnapshot(latestData),
          },
          unfinishedSearchAnalyticsOnboardingSessions:
            result.data?.unfinishedSearchAnalyticsOnboardingSessions ??
            latestData?.unfinishedSearchAnalyticsOnboardingSessions ??
            [],
          completedSearchAnalyticsOnboardingSessions:
            result.data?.completedSearchAnalyticsOnboardingSessions ??
            latestData?.completedSearchAnalyticsOnboardingSessions ??
            [],
          unfinishedUserAnalyticsOnboardingSessions:
            result.data?.unfinishedUserAnalyticsOnboardingSessions ??
            latestData?.unfinishedUserAnalyticsOnboardingSessions ??
            [],
          completedUserAnalyticsOnboardingSessions:
            result.data?.completedUserAnalyticsOnboardingSessions ??
            latestData?.completedUserAnalyticsOnboardingSessions ??
            [],
        } as WorkspaceSidebarData,
        error: result.error,
      };
    }
    return {
      data: null,
      error: projectsResult.error ?? result.error,
    };
  }

  if (result.error && isProjectOnlyError(result.error) && !result.data?.node) {
    // Project配下のnon-null resolver障害もOrganization node全体へ伝播する。
    // 障害時だけDashboardを別queryで回収し、refreshなら直前のProject snapshotを
    // 維持することで、片方の障害でsidebar全体を失わないようにする。
    const dashboardsResult = await client
      .query(
        WorkspaceSidebarDashboardsDataQuery,
        { organizationId: workspaceId },
        { requestPolicy: "network-only" },
      )
      .toPromise();
    if (dashboardsResult.data?.node && !dashboardsResult.error) {
      const latestData = getLatestData();
      return {
        data: {
          ...(result.data ?? latestData),
          node: {
            ...projectSnapshot(latestData),
            ...dashboardsResult.data.node,
          },
          unfinishedSearchAnalyticsOnboardingSessions:
            result.data?.unfinishedSearchAnalyticsOnboardingSessions ??
            latestData?.unfinishedSearchAnalyticsOnboardingSessions ??
            [],
          completedSearchAnalyticsOnboardingSessions:
            result.data?.completedSearchAnalyticsOnboardingSessions ??
            latestData?.completedSearchAnalyticsOnboardingSessions ??
            [],
          unfinishedUserAnalyticsOnboardingSessions:
            result.data?.unfinishedUserAnalyticsOnboardingSessions ??
            latestData?.unfinishedUserAnalyticsOnboardingSessions ??
            [],
          completedUserAnalyticsOnboardingSessions:
            result.data?.completedUserAnalyticsOnboardingSessions ??
            latestData?.completedUserAnalyticsOnboardingSessions ??
            [],
        } as WorkspaceSidebarData,
        error: result.error,
      };
    }
    return {
      data: null,
      error: dashboardsResult.error ?? result.error,
    };
  }

  // nullableなProject fieldだけのresolver errorではOrganization nodeとProject配列が
  // 利用できる。non-null propagation時のfallbackへ入れず、cold loadではそのpartial
  // dataを表示し、refresh時の採否はProviderのlast-success contractへ委ねる。

  if (!result.data && result.error && isOnboardingOnlyError(result.error)) {
    // Onboarding fieldはtop-level non-nullなので、そのresolverだけの障害でも
    // 統合query全体がnullになり得る。通常経路を分割せず、障害時に限って
    // core navigationを再取得し、ProjectとDashboardを巻き込まないようにする。
    const coreResult = await client
      .query(
        WorkspaceSidebarCoreDataQuery,
        { organizationId: workspaceId },
        { requestPolicy: "network-only" },
      )
      .toPromise();
    if (coreResult.data && !coreResult.error) {
      // cold requestとrefreshが並行した場合も、request開始時ではなくfallback完了時の
      // 最新snapshotを参照し、途中で取得できたOnboarding sessionを消さない。
      const latestData = getLatestData();
      return {
        data: {
          ...coreResult.data,
          unfinishedSearchAnalyticsOnboardingSessions:
            latestData?.unfinishedSearchAnalyticsOnboardingSessions ?? [],
          completedSearchAnalyticsOnboardingSessions:
            latestData?.completedSearchAnalyticsOnboardingSessions ?? [],
          unfinishedUserAnalyticsOnboardingSessions:
            latestData?.unfinishedUserAnalyticsOnboardingSessions ?? [],
          completedUserAnalyticsOnboardingSessions:
            latestData?.completedUserAnalyticsOnboardingSessions ?? [],
        } as WorkspaceSidebarData,
        error: result.error,
      };
    }
    return {
      data: null,
      error: coreResult.error ?? result.error,
    };
  }

  return result;
}
